import type { LocationQuery } from 'vue-router';

import { IntegrationLoginProvider } from '~kui/types/auth';

import type { ProviderLoginFailure } from '~/graphql/generated/graphql';

import { UNIFIED_ROUTE_AUTH_FAILED } from '~/constants/routes/unified';
import { IntegrationProvider } from '~/graphql/generated/graphql';

/**
 * A launch from the provider's portal (portal tile, instant-login link) is sent back there;
 * bouncing a portal user to our login page is the one thing Clever certification forbids.
 * Clever is the only portal-launching provider so far.
 */
export const PROVIDER_PORTAL_URL = 'https://clever.com/';

/**
 * The provider behind each login button key, which is also the slug in the provider login
 * page's path (`/auth/{slug}/callback`, the redirect URI registered with the provider). The fake
 * profile is a separate slug so a test or demo presses the real button against the scriptable fake.
 */
export const PROVIDERS: Record<IntegrationLoginProvider, IntegrationProvider> = {
  [IntegrationLoginProvider.Clever]: IntegrationProvider.Clever,
  [IntegrationLoginProvider.FakeClever]: IntegrationProvider.FakeClever,
};

/**
 * A login attempt id is a ULID (lowercase as Laravel writes it), whose first character is 0 to 7
 * as the server requires. Anything else in a query is not
 * ours, and showing it, or sending it back, would let a crafted link put its own words on our
 * pages ("Reference: call …").
 */
const LOGIN_ATTEMPT_ID = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/i;

export const isLoginAttemptId = (value: unknown): value is string => typeof value === 'string' && LOGIN_ATTEMPT_ID.test(value);

/**
 * The provider failure page for a refused login: its screen reason, the attempt id as the
 * reference code, and whether the page sends the person back to the provider's portal.
 */
export const providerLoginFailureLocation = ({ screen, attemptId, launchedFromProvider }: Pick<ProviderLoginFailure, 'screen' | 'attemptId' | 'launchedFromProvider'>) => ({
  path: clusterRedirectPath(UNIFIED_ROUTE_AUTH_FAILED),
  query: {
    reason: screen.toLowerCase(),
    attempt: attemptId,
    ...(launchedFromProvider ? { launch: 'provider' } : {}),
  },
});

/** A provider sign-in the login page finishes with the student's choice of school account. */
export interface ProviderLoginChoice {
  provider: IntegrationLoginProvider;
  /** Whether a code emailed to the address the provider asserted proved the sign-in. */
  viaProof: boolean;
}

const isIntegrationLoginProvider = (value: unknown): value is IntegrationLoginProvider =>
  (Object.values(IntegrationLoginProvider) as unknown[]).includes(value);

/** The query that tells the login page which provider sign-in its choice of account finishes. */
export const providerLoginChoiceQuery = ({ provider, viaProof }: ProviderLoginChoice) => ({
  integration_provider: provider,
  ...(viaProof ? { via_proof: '1' } : {}),
});

/** The provider sign-in a login page's query names, or null when it names no integration provider. */
export const providerLoginChoiceFromQuery = (query: LocationQuery): ProviderLoginChoice | null => {
  const provider = query.integration_provider;
  return isIntegrationLoginProvider(provider) ? { provider, viaProof: query.via_proof === '1' } : null;
};
